/**
 * LineItemHierarchy — IO + line-item table rendered from the DV360/TTD final
 * plan. Inline edits emit JSON-Pointer paths into that same plan:
 *   /insertionOrders/<ioIdx>/displayName
 *   /insertionOrders/<ioIdx>/pacing/dailyMaxImpressions
 *   /insertionOrders/<ioIdx>/budget/budgetSegments/0/budgetAmountInImpressions
 *   /lineItems/<liIdx>/displayName
 *   /lineItems/<liIdx>/flight/dateRange/startDate
 *   /lineItems/<liIdx>/flight/dateRange/endDate
 *   /lineItems/<liIdx>/pacing/dailyMaxImpressions
 *
 * These are paths into `currentPlan` — the reviewer-owned editedPlan.
 */

import React, { useState } from 'react';
import { formatDvDate, planTerms } from './planSelectors';

const fmtImp = (n) => (Number(n) || 0).toLocaleString();

function resolveIoLineItems(plan, io) {
  const refs = Array.isArray(io.lineItems) ? io.lineItems : [];
  const lookup = new Map();
  for (const li of plan.lineItems || []) {
    if (li && li.lineItemId) lookup.set(li.lineItemId, li);
  }
  return refs
    .map(ref => ({ ref, li: typeof ref === 'string' ? lookup.get(ref) : ref }))
    .filter(x => x.li);
}

function lineItemIndex(plan, li) {
  return (plan.lineItems || []).findIndex(x => x && x.lineItemId === li.lineItemId);
}

// DV360 line items that use `flightDateType: LINE_ITEM_FLIGHT_DATE_TYPE_INHERITED`
// don't carry their own dateRange — the flight comes from the parent IO's
// budget segment. Resolve with that fallback so the Flight column isn't blank
// on every row of every plan.
function resolveLineItemFlight(li, parentIo) {
  const custom = li.flight?.dateRange;
  if (custom?.startDate || custom?.endDate) {
    return {
      startDate: formatDvDate(custom.startDate),
      endDate: formatDvDate(custom.endDate),
      source: 'line_item',
    };
  }
  const seg = parentIo?.budget?.budgetSegments?.[0]?.dateRange;
  if (seg?.startDate || seg?.endDate) {
    return {
      startDate: formatDvDate(seg.startDate),
      endDate: formatDvDate(seg.endDate),
      source: 'io_inherited',
    };
  }
  return { startDate: null, endDate: null, source: 'none' };
}

// Standalone inline-edit widget with its own draft state. Used for fields
// where only one edit target exists (IO name, LI name) — cheaper than
// threading a shared editingField through a parent.
const InlineEdit = ({ value, placeholder, onCommit, className = 'li-editable', inputClassName = 'li-input' }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const begin = () => {
    setDraft(value == null ? '' : String(value));
    setEditing(true);
  };
  const commit = () => {
    if (!editing) return;
    const next = draft;
    setEditing(false);
    if (next !== (value == null ? '' : String(value))) onCommit(next);
  };

  return editing ? (
    <input
      autoFocus
      className={inputClassName}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') setEditing(false);
      }}
    />
  ) : (
    <button className={className} onClick={begin} title="Click to edit">
      {value || placeholder || '—'}
    </button>
  );
};

const LineItemRow = ({ li, parentIo, ioIdx, liIdx, onEditField, onReferenceLineItem }) => {
  const [editingField, setEditingField] = useState(null);
  const [draftValue, setDraftValue] = useState('');

  const displayName = li.displayName || '—';
  const tactic = li.tactic || '—';
  const { startDate, endDate, source: flightSource } = resolveLineItemFlight(li, parentIo);
  const dailyMax = li.pacing?.dailyMaxImpressions;
  // TTD ad-group "Daily Max" is derived (parent Campaign cap ÷ sibling
  // count) — see normalizeForDashboard. Prefix with ≈ so reviewers don't
  // mistake it for a stored, directly-edited value.
  const dailyMaxEstimated = Boolean(li.pacing?.__estimated);

  // Inline edits emit DV360-shaped JSON-Pointer paths (`/lineItems/N/...`).
  // For TTD plans the dashboard runs against a normalized object whose
  // entries carry __refPath ("/AdGroups/N", "/Campaigns/N") — those paths
  // wouldn't match the agent's apply_plan_edit whitelist (still DV360-only)
  // and the indices wouldn't even map back to the raw plan. Until TTD edit
  // support lands, render TTD rows read-only.
  const isReadOnly = Boolean(li.__refPath || parentIo.__refPath);

  const beginEdit = (field, current) => {
    setEditingField(field);
    setDraftValue(current == null ? '' : String(current));
  };

  const commitEdit = () => {
    if (editingField == null) return;
    let coerced = draftValue;
    let path;
    let reason;

    switch (editingField) {
      case 'startDate':
        // If this LI inherits flight from its IO, editing the LI itself
        // won't do anything — target the IO's budget segment instead.
        if (flightSource === 'io_inherited') {
          path = `/insertionOrders/${ioIdx}/budget/budgetSegments/0/dateRange/startDate`;
          reason = `Inline edit: IO ${ioIdx} budgetSegments[0].startDate → ${draftValue} (LI inherits flight)`;
        } else {
          path = `/lineItems/${liIdx}/flight/dateRange/startDate`;
          reason = `Inline edit: lineItems[${liIdx}].flight.startDate → ${draftValue}`;
        }
        break;
      case 'endDate':
        if (flightSource === 'io_inherited') {
          path = `/insertionOrders/${ioIdx}/budget/budgetSegments/0/dateRange/endDate`;
          reason = `Inline edit: IO ${ioIdx} budgetSegments[0].endDate → ${draftValue} (LI inherits flight)`;
        } else {
          path = `/lineItems/${liIdx}/flight/dateRange/endDate`;
          reason = `Inline edit: lineItems[${liIdx}].flight.endDate → ${draftValue}`;
        }
        break;
      case 'dailyMax':
        coerced = Number(draftValue);
        if (!Number.isFinite(coerced)) { setEditingField(null); return; }
        path = `/lineItems/${liIdx}/pacing/dailyMaxImpressions`;
        reason = `Inline edit: lineItems[${liIdx}].pacing.dailyMaxImpressions → ${coerced}`;
        break;
      default:
        setEditingField(null);
        return;
    }

    onEditField({ path, newValue: coerced, reason });
    setEditingField(null);
  };

  const Editable = ({ field, display, current }) => (
    editingField === field ? (
      <input
        autoFocus
        className="li-input"
        value={draftValue}
        onChange={e => setDraftValue(e.target.value)}
        onBlur={commitEdit}
        onKeyDown={e => {
          if (e.key === 'Enter') commitEdit();
          if (e.key === 'Escape') setEditingField(null);
        }}
      />
    ) : (
      <button
        className="li-editable"
        onClick={() => beginEdit(field, current)}
        title="Click to edit"
      >{display}</button>
    )
  );

  return (
    <tr className="li-row">
      <td className="li-name">
        {isReadOnly ? (
          <span className="li-readonly">{displayName}</span>
        ) : (
          <InlineEdit
            value={displayName === '—' ? '' : displayName}
            placeholder="—"
            onCommit={(next) => {
              if (next.trim() === '') return;
              onEditField({
                path: `/lineItems/${liIdx}/displayName`,
                newValue: next,
                reason: `Inline edit: lineItems[${liIdx}].displayName → ${next}`,
              });
            }}
            inputClassName="li-input li-input-wide"
          />
        )}
      </td>
      <td className="li-tactic">{tactic}</td>
      <td className="li-num">
        {dailyMax == null ? (
          <span className="li-muted">—</span>
        ) : isReadOnly ? (
          <span
            className="li-readonly"
            title={dailyMaxEstimated
              ? "Estimated: parent Campaign's daily cap split evenly across its ad groups. TTD doesn't store a per-AG daily cap."
              : undefined}
          >{dailyMaxEstimated ? '≈ ' : ''}{fmtImp(dailyMax)}</span>
        ) : (
          <Editable field="dailyMax" display={fmtImp(dailyMax)} current={dailyMax} />
        )}
      </td>
      <td className="li-date">
        {isReadOnly ? (
          <>
            <span className="li-readonly">{startDate || '—'}</span>
            <span className="li-dash">—</span>
            <span className="li-readonly">{endDate || '—'}</span>
          </>
        ) : (
          <>
            <Editable field="startDate" display={startDate || '—'} current={startDate} />
            <span className="li-dash">—</span>
            <Editable field="endDate" display={endDate || '—'} current={endDate} />
          </>
        )}
      </td>
      {onReferenceLineItem && (
        <td className="li-ref-cell">
          <button
            className="li-ref-btn"
            onClick={() => onReferenceLineItem({
              // Prefer the platform-native path on the normalized object
              // (TTD: /AdGroups/N). DV360 rows never carry __refPath, so
              // fall through to the legacy /lineItems/N construction.
              path: li.__refPath || `/lineItems/${liIdx}`,
              displayName: li.displayName || `line item ${liIdx}`,
            })}
            title="Drop a reference to this line item into the Copilot input"
            aria-label="Reference this line item in the Copilot"
          >@</button>
        </td>
      )}
    </tr>
  );
};

const LineItemHierarchy = ({ plan, onEditField, onReferenceLineItem }) => {
  const ios = plan.insertionOrders || [];
  const terms = planTerms(plan);
  if (ios.length === 0) {
    return (
      <div className="card">
        <div className="card-title">{terms.lis}</div>
        <div className="li-empty">No {terms.ios.toLowerCase()} in the plan.</div>
      </div>
    );
  }
  return (
    <div className="card">
      <div className="card-title">{terms.lis}</div>
      {ios.map((io, ioIdx) => {
        const resolved = resolveIoLineItems(plan, io);
        const ioImpressions = (io.budget?.budgetSegments || [])
          .reduce((acc, s) => acc + Number(s.budgetAmountInImpressions || 0), 0);
        return (
          <div key={ioIdx} className="io-block">
            <div className="io-header">
              <span className="io-name">
                {io.__refPath ? (
                  <span className="io-name-readonly">{io.displayName || `IO ${ioIdx + 1}`}</span>
                ) : (
                  <InlineEdit
                    value={io.displayName || ''}
                    placeholder={`IO ${ioIdx + 1}`}
                    onCommit={(next) => {
                      if (next.trim() === '') return;
                      onEditField({
                        path: `/insertionOrders/${ioIdx}/displayName`,
                        newValue: next,
                        reason: `Inline edit: insertionOrders[${ioIdx}].displayName → ${next}`,
                      });
                    }}
                    className="io-name-editable"
                    inputClassName="li-input li-input-wide"
                  />
                )}
              </span>
              <span className="io-meta">
                {fmtImp(ioImpressions)} imps
                {io.pacing?.dailyMaxImpressions && <> · {terms.ioDailyCapLabel} {fmtImp(io.pacing.dailyMaxImpressions)}</>}
                {' · '}{resolved.length} {terms.liShort}{resolved.length === 1 ? '' : 's'}
              </span>
            </div>
            <table className="li-table">
              <thead>
                <tr>
                  <th>{terms.li}</th><th>Tactic</th>
                  <th>Daily Max (imps)</th><th>Flight</th>
                  {onReferenceLineItem && <th className="li-ref-col" aria-label="Reference"></th>}
                </tr>
              </thead>
              <tbody>
                {resolved.map(({ li }, j) => (
                  <LineItemRow
                    key={j}
                    li={li}
                    parentIo={io}
                    ioIdx={ioIdx}
                    liIdx={lineItemIndex(plan, li)}
                    onEditField={onEditField}
                    onReferenceLineItem={onReferenceLineItem}
                  />
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
      <style>{`
        .io-block { margin-bottom: 18px; }
        .io-header { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid #eee; }
        .io-name { font-weight: 600; font-size: 13px; }
        .io-meta { font-size: 12px; color: #777; }
        .li-empty { font-size: 12px; color: #999; padding: 10px 0; }
        .li-muted { color: #bbb; }
        .li-table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 4px; }
        .li-table th { text-align: left; padding: 6px 8px; color: #666; font-weight: 500; border-bottom: 1px solid #eee; }
        .li-table td { padding: 6px 8px; border-bottom: 1px solid #f5f5f5; vertical-align: middle; }
        .li-name { color: #222; }
        .li-num { font-variant-numeric: tabular-nums; text-align: right; }
        .li-dash { margin: 0 4px; color: #999; }
        .li-editable {
          background: none; border: 1px dashed transparent; padding: 1px 4px;
          border-radius: 3px; cursor: pointer; color: inherit;
          font: inherit; font-variant-numeric: tabular-nums;
          text-align: left;
        }
        .li-editable:hover { border-color: #cde; background: #f5fafd; }
        .li-input {
          width: 100px; padding: 2px 4px; border: 1px solid #8cb; border-radius: 3px;
          font: inherit; font-variant-numeric: tabular-nums;
        }
        .li-input-wide { width: 260px; }
        .io-name-editable {
          background: none; border: 1px dashed transparent; padding: 1px 6px;
          border-radius: 3px; cursor: pointer; color: inherit;
          font: inherit; font-weight: 600; font-size: 13px; text-align: left;
        }
        .io-name-editable:hover { border-color: #cde; background: #f5fafd; }
        .li-readonly {
          padding: 1px 4px; color: inherit;
          font: inherit; font-variant-numeric: tabular-nums;
        }
        .io-name-readonly {
          padding: 1px 6px; color: inherit;
          font: inherit; font-weight: 600; font-size: 13px;
        }
        .li-ref-col { width: 28px; }
        .li-ref-cell { width: 28px; text-align: center; }
        .li-ref-btn {
          width: 22px; height: 22px; padding: 0; line-height: 1;
          background: #fff; border: 1px solid #d0d7de; border-radius: 4px;
          color: #57606a; cursor: pointer; font-size: 12px; font-weight: 600;
        }
        .li-ref-btn:hover {
          background: #0969da; color: white; border-color: #0969da;
        }
      `}</style>
    </div>
  );
};

export default LineItemHierarchy;
